import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { ProjectFile } from '../types';
import {
  VerifierPromptOutputRequest,
  VerifierPromptOutputResponse,
  VerifierRunMode,
  VerifierRunPayload,
  VerifierRunPhase,
  VerifierRunStatus,
  VerifierRunTicketRequest,
  VerifierRunTicketResponse,
  VerifierStreamEvent,
  VerifierUserFacingError,
} from '../types/verifier';
import {
  downloadFileContentFromStorage,
  formatBytes,
  isStorageBackedFile,
} from './projects';

const CREATE_VERIFIER_RUN_TICKET_TIMEOUT_MS = 30000;
const RUN_PROMPT_TEX_TIMEOUT_MS = 540000;
const DEFAULT_LOCAL_VERIFIER_URL = 'http://127.0.0.1:8080';
const MAX_FILE_COUNT = 64;
const MAX_TOTAL_BYTES = 5 * 1024 * 1024;

function inferRunMode(entrypoint: string): VerifierRunMode {
  const lower = entrypoint.toLowerCase();
  if (lower.endsWith('.lean')) return 'lean-proof';
  if (lower.endsWith('.py')) return 'python-file';
  throw new Error('Verifier entrypoint must be a .py or .lean file.');
}

interface CallableErrorDetailsLike {
  reason?: unknown;
  retryable?: unknown;
}

interface CallableErrorLike {
  code?: unknown;
  details?: unknown;
  message?: unknown;
}

const createVerifierRunTicketFn = httpsCallable<
  VerifierRunTicketRequest,
  VerifierRunTicketResponse
>(
  functions,
  'createVerifierRunTicket',
  { timeout: CREATE_VERIFIER_RUN_TICKET_TIMEOUT_MS }
);

const runPromptTexFn = httpsCallable<
  VerifierPromptOutputRequest,
  VerifierPromptOutputResponse
>(
  functions,
  'runPromptTex',
  { timeout: RUN_PROMPT_TEX_TIMEOUT_MS }
);

type JsonLike =
  | null
  | boolean
  | number
  | string
  | JsonLike[]
  | { [key: string]: JsonLike };

function sortJsonLike(value: JsonLike): JsonLike {
  if (Array.isArray(value)) {
    return value.map((item) => sortJsonLike(item));
  }

  if (value && typeof value === 'object') {
    const sortedEntries = Object.keys(value)
      .sort()
      .map((key) => [key, sortJsonLike((value as { [key: string]: JsonLike })[key])] as const);

    return sortedEntries.reduce<{ [key: string]: JsonLike }>((accumulator, [key, sortedValue]) => {
      accumulator[key] = sortedValue;
      return accumulator;
    }, {});
  }

  return value;
}

export function normalizeVerifierPath(rawName: string): string {
  const trimmed = rawName.trim().replace(/\\/g, '/');
  if (!trimmed) {
    throw new Error('File names cannot be empty.');
  }

  if (trimmed.startsWith('/') || trimmed.startsWith('.')) {
    throw new Error(`Invalid verifier file path: ${rawName}`);
  }

  const parts = trimmed.split('/');
  if (parts.some((part) => !part || part === '.' || part === '..')) {
    throw new Error(`Invalid verifier file path: ${rawName}`);
  }

  return parts.join('/');
}

const VERIFIER_REQUIRES_PATTERNS = [
  /^#\s*@verifier-requires:\s*(.+)/,   // Python: # @verifier-requires: file.py
  /^--\s*@verifier-requires:\s*(.+)/,   // Lean:   -- @verifier-requires: file.lean
];

export function resolveProjectFilePath(file: ProjectFile, allFiles: ProjectFile[]): string {
  const parts: string[] = [file.name];
  let currentParentId = file.parentId;

  while (currentParentId) {
    const parent = allFiles.find((f) => f.id === currentParentId);
    if (!parent || parent.type !== 'folder') break;
    parts.unshift(parent.name);
    currentParentId = parent.parentId;
  }

  return parts.join('/');
}

export function parseVerifierRequires(scriptContent: string): string[] {
  const requires: string[] = [];
  for (const line of scriptContent.split('\n')) {
    for (const pattern of VERIFIER_REQUIRES_PATTERNS) {
      const match = line.match(pattern);
      if (match) {
        const rawPath = match[1].trim();
        if (rawPath) {
          requires.push(normalizeVerifierPath(rawPath));
        }
        break;
      }
    }
  }

  return Array.from(new Set(requires));
}

export interface SelectedVerifierFiles {
  selected: ProjectFile[];
  needsStorageFetch: ProjectFile[];
}

export function selectVerifierFiles(
  entrypointFile: ProjectFile,
  allFiles: ProjectFile[]
): SelectedVerifierFiles {
  const requires = parseVerifierRequires(entrypointFile.content);

  let selected: ProjectFile[];

  if (requires.length === 0) {
    // No pragmas: all non-folder/non-image files (current behavior),
    // but skip storage-backed files unless explicitly required.
    selected = allFiles.filter((f) =>
      f.type !== 'folder' && f.type !== 'image' && !isStorageBackedFile(f)
    );
  } else {
    // Build a path map for lookup
    const pathMap = new Map<string, ProjectFile>();
    for (const file of allFiles) {
      if (file.type === 'folder') continue;
      const fullPath = resolveProjectFilePath(file, allFiles);
      pathMap.set(fullPath, file);
    }

    const entrypointPath = resolveProjectFilePath(entrypointFile, allFiles);
    selected = [entrypointFile];

    const missing: string[] = [];
    for (const requiredPath of requires) {
      if (requiredPath === entrypointPath) continue;
      const found = pathMap.get(requiredPath);
      if (!found) {
        missing.push(requiredPath);
      } else if (!selected.some((s) => s.id === found.id)) {
        selected.push(found);
      }
    }

    if (missing.length > 0) {
      const error = new Error(
        `verifier.py requires files not found in this project: ${missing.join(', ')}. `
        + 'Create the missing files or remove the @verifier-requires directives.'
      );
      (error as Error & { code: string }).code = 'required-file-missing';
      throw error;
    }
  }

  const needsStorageFetch = selected.filter((f) => isStorageBackedFile(f));
  return { selected, needsStorageFetch };
}

export async function resolveVerifierFileContents(
  files: ProjectFile[],
  allFiles: ProjectFile[],
  onProgress?: (fileName: string, sizeBytes: number) => void
): Promise<ProjectFile[]> {
  return Promise.all(
    files.map(async (file) => {
      if (!isStorageBackedFile(file) || !file.storageContentPath) return file;
      onProgress?.(file.name, file.storageSizeBytes ?? 0);
      try {
        const content = await downloadFileContentFromStorage(file.storageContentPath);
        return { ...file, content };
      } catch (err) {
        const error = new Error(
          `Failed to download ${file.name} from storage. The file may have been deleted or is temporarily unavailable.`
        );
        (error as Error & { code: string }).code = 'storage-fetch-failed';
        throw error;
      }
    })
  );
}

export function normalizeVerifierPayloadError(error: unknown): VerifierUserFacingError | null {
  if (!(error instanceof Error)) return null;

  const code = (error as Error & { code?: string }).code;

  if (code === 'required-file-missing') {
    return {
      code: 'required-file-missing',
      phase: 'verifier',
      title: 'Required file not found',
      message: error.message,
      guidance: 'Add the missing file to your project or remove the @verifier-requires directive from verifier.py.',
      retryable: false,
    };
  }

  if (code === 'storage-fetch-failed') {
    return {
      code: 'storage-fetch-failed',
      phase: 'verifier',
      title: 'Failed to download a required file',
      message: error.message,
      guidance: 'Try re-uploading the file and running the verifier again.',
      retryable: true,
    };
  }

  if (error.message.includes('byte limit') || error.message.includes('exceeds the')) {
    return {
      code: 'payload-too-large',
      phase: 'verifier',
      title: 'Verifier payload is too large',
      message: error.message,
      guidance: 'Use @verifier-requires directives in verifier.py to send only the files your script needs.',
      retryable: false,
    };
  }

  return null;
}

export function buildVerifierRunPayload(
  projectId: string,
  entrypoint: string,
  files: ProjectFile[],
  options?: {
    stdinText?: string;
    allFiles?: ProjectFile[];
  }
): VerifierRunPayload {
  const allFilesForPaths = options?.allFiles ?? files;

  const snapshotFiles = files
    .filter((file) => file.type !== 'folder' && file.type !== 'image')
    .slice()
    .sort((left, right) => {
      const nameComparison = left.name.localeCompare(right.name);
      if (nameComparison !== 0) {
        return nameComparison;
      }
      return left.id.localeCompare(right.id);
    })
    .map((file) => ({
      name: normalizeVerifierPath(resolveProjectFilePath(file, allFilesForPaths)),
      content: file.content,
      type: file.type,
    }));

  if (snapshotFiles.length < 1) {
    throw new Error('There are no text files available to send to the verifier.');
  }

  if (snapshotFiles.length > MAX_FILE_COUNT) {
    throw new Error(`Projects with more than ${MAX_FILE_COUNT} files cannot be run in the verifier.`);
  }

  const duplicatePaths = new Set<string>();
  for (const file of snapshotFiles) {
    if (duplicatePaths.has(file.name)) {
      throw new Error(`Duplicate verifier file path: ${file.name}`);
    }
    duplicatePaths.add(file.name);
  }

  const normalizedEntrypoint = normalizeVerifierPath(entrypoint);
  const runMode = inferRunMode(normalizedEntrypoint);

  const totalBytes = snapshotFiles.reduce(
    (sum, file) => sum + new TextEncoder().encode(file.content).length,
    0
  );

  if (totalBytes > MAX_TOTAL_BYTES) {
    throw new Error(
      `Verifier payload is ${formatBytes(totalBytes)}, which exceeds the ${formatBytes(MAX_TOTAL_BYTES)} limit. `
      + 'Use @verifier-requires directives to send only the files your verifier needs.'
    );
  }

  if (!snapshotFiles.some((file) => file.name === normalizedEntrypoint)) {
    throw new Error(`The active Python file ${normalizedEntrypoint} is missing from the current snapshot.`);
  }

  const stdinText = options?.stdinText ?? '';
  if (typeof stdinText !== 'string') {
    throw new Error('Verifier stdinText must be a string.');
  }

  return {
    projectId,
    entrypoint: normalizedEntrypoint,
    runMode,
    stdinText,
    args: [],
    files: snapshotFiles,
  };
}

export function getPromptTexFile(files: ProjectFile[]): ProjectFile {
  const promptFile = files.find((file) => (
    file.type !== 'folder'
    && file.type !== 'image'
    && file.name.trim().toLowerCase() === 'prompt.tex'
  ));

  if (!promptFile) {
    throw new Error('This project must include prompt.tex before the verifier can run.');
  }

  return promptFile;
}

export function canonicalizeVerifierRunPayload(payload: VerifierRunPayload): string {
  return JSON.stringify(sortJsonLike(payload as unknown as JsonLike));
}

export async function hashVerifierRunPayload(payload: VerifierRunPayload): Promise<string> {
  const cryptoApi = globalThis.crypto;
  if (!cryptoApi?.subtle) {
    throw new Error('Web Crypto is unavailable in this browser.');
  }

  const data = new TextEncoder().encode(canonicalizeVerifierRunPayload(payload));
  const digest = await cryptoApi.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

export function getVerifierPayloadSizeBytes(payload: VerifierRunPayload): number {
  return payload.files.reduce(
    (sum, file) => sum + new TextEncoder().encode(file.content).length,
    0
  );
}

export function getVerifierServiceUrl(): string {
  const configured = process.env.REACT_APP_VERIFIER_SERVICE_URL?.trim();
  if (configured) {
    return configured.replace(/\/+$/, '');
  }

  if (window.location.hostname === 'localhost') {
    return DEFAULT_LOCAL_VERIFIER_URL;
  }

  throw new Error('Verifier service URL is not configured.');
}

function buildVerifierFetchErrorMessage(serviceUrl: string, error: unknown): string {
  if (window.location.hostname === 'localhost') {
    return `Could not reach the local verifier service at ${serviceUrl}. Start it with npm run dev or npm run dev:verifier and try again.`;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return 'Could not reach the verifier service.';
}

function normalizeCallableErrorCode(rawCode: unknown): string {
  if (typeof rawCode !== 'string') {
    return '';
  }

  return rawCode.replace(/^functions\//, '');
}

function getCallableErrorDetails(error: unknown): CallableErrorDetailsLike | null {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const details = (error as CallableErrorLike).details;
  if (!details || typeof details !== 'object') {
    return null;
  }

  return details as CallableErrorDetailsLike;
}

function getCallableErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (error && typeof error === 'object' && typeof (error as CallableErrorLike).message === 'string') {
    return (error as CallableErrorLike).message as string;
  }

  return '';
}

function inferPromptFailureReason(error: unknown, message: string): string {
  const details = getCallableErrorDetails(error);
  if (typeof details?.reason === 'string' && details.reason.trim()) {
    return details.reason.trim();
  }

  const normalizedMessage = message.toLowerCase();
  if (normalizedMessage.includes('empty response')) {
    return 'ai-empty-response';
  }

  if (
    normalizedMessage.includes('timed out')
    || normalizedMessage.includes('timeout')
    || normalizedMessage.includes('deadline')
  ) {
    return 'ai-timeout';
  }

  if (
    normalizedMessage.includes('too large')
    || normalizedMessage.includes('context length')
    || normalizedMessage.includes('too many tokens')
    || normalizedMessage.includes('input too long')
  ) {
    return 'ai-request-too-large';
  }

  return '';
}

export function normalizeVerifierPromptError(error: unknown): VerifierUserFacingError {
  const rawCode = error && typeof error === 'object'
    ? (error as CallableErrorLike).code
    : undefined;
  const code = normalizeCallableErrorCode(rawCode);
  const message = getCallableErrorMessage(error);
  const reason = inferPromptFailureReason(error, message);

  if (reason === 'ai-timeout' || code === 'deadline-exceeded') {
    return {
      code: 'ai-timeout',
      phase: 'prompt',
      title: 'The AI model did not respond in time',
      message: 'The verifier waited for prompt.tex to finish, but the model never returned verifier input.',
      guidance: 'Try again. If this keeps happening, shorten prompt.tex or ask for a smaller final answer format.',
      retryable: true,
    };
  }

  if (reason === 'ai-empty-response') {
    return {
      code: 'ai-empty-response',
      phase: 'prompt',
      title: 'The AI model returned no usable output',
      message: 'prompt.tex completed without producing the candidate answer that verifier.py needs on stdin.',
      guidance: 'Tighten prompt.tex so it asks for a single final answer with no explanation, then rerun the verifier.',
      retryable: true,
    };
  }

  if (reason === 'ai-request-too-large') {
    return {
      code: 'ai-request-too-large',
      phase: 'prompt',
      title: 'prompt.tex is too large for the AI model',
      message: 'The verifier could not send this prompt to the model successfully.',
      guidance: 'Shorten prompt.tex or reduce how much output it asks for, then try again.',
      retryable: true,
    };
  }

  if (reason === 'ai-rate-limited' || code === 'resource-exhausted') {
    return {
      code: 'ai-rate-limited',
      phase: 'prompt',
      title: 'The AI model is busy right now',
      message: 'The verifier request was rate limited before prompt.tex could finish.',
      guidance: 'Wait a moment and run the verifier again.',
      retryable: true,
    };
  }

  if (reason === 'ai-unavailable' || code === 'unavailable') {
    return {
      code: 'ai-unavailable',
      phase: 'prompt',
      title: 'The AI model service is unavailable',
      message: 'The verifier could not get a response back from the model service.',
      guidance: 'Retry shortly. If it keeps failing, there may be an upstream outage.',
      retryable: true,
    };
  }

  if (reason === 'configuration' || code === 'failed-precondition') {
    return {
      code: 'configuration',
      phase: 'prompt',
      title: 'Verifier AI is not configured',
      message: message || 'The server is missing the configuration needed to run prompt.tex through the model.',
      guidance: 'Ask an administrator to configure the verifier AI secret before retrying.',
      retryable: false,
    };
  }

  if (code === 'invalid-argument') {
    return {
      code: 'invalid-prompt',
      phase: 'prompt',
      title: 'prompt.tex cannot be run yet',
      message: message || 'prompt.tex is missing the input required for the AI stage.',
      guidance: 'Fix prompt.tex and run the verifier again.',
      retryable: false,
    };
  }

  return {
    code: reason === 'ai-error' ? 'ai-error' : 'unknown',
    phase: 'prompt',
    title: 'The AI request failed before the verifier could start',
    message: 'The verifier could not turn prompt.tex into verifier input.',
    guidance: 'Try again. If this keeps happening, there may be an upstream model issue.',
    retryable: true,
  };
}

export function formatVerifierErrorForLog(error: VerifierUserFacingError): string {
  return error.guidance
    ? `${error.title}: ${error.message} ${error.guidance}`
    : `${error.title}: ${error.message}`;
}

export function getVerifierStatusForError(
  error: VerifierUserFacingError,
  fallback: VerifierRunStatus = 'failed'
): VerifierRunStatus {
  if (error.code === 'ai-timeout') {
    return 'timed_out';
  }

  return fallback;
}

export function getVerifierStatusLabel(
  status: VerifierRunStatus,
  phase: VerifierRunPhase = 'idle'
): string {
  if (status === 'starting' && phase === 'prompt') {
    return 'Waiting On AI';
  }

  if (status === 'starting' && phase === 'verifier') {
    return 'Preparing';
  }

  switch (status) {
    case 'starting':
      return 'Starting';
    case 'running':
      return 'Running';
    case 'success':
      return 'Success';
    case 'failed':
      return 'Failed';
    case 'timed_out':
      return 'Timed Out';
    case 'aborted':
      return 'Aborted';
    default:
      return 'Idle';
  }
}

export function parseVerifierStreamEvent(line: string): VerifierStreamEvent {
  const parsed = JSON.parse(line) as Partial<VerifierStreamEvent>;
  if (!parsed || typeof parsed !== 'object' || typeof parsed.type !== 'string') {
    throw new Error('Invalid verifier event payload.');
  }

  switch (parsed.type) {
    case 'started':
      if (typeof parsed.runId !== 'string' || typeof parsed.entrypoint !== 'string') {
        throw new Error('Invalid verifier started event.');
      }
      return {
        type: 'started',
        runId: parsed.runId,
        entrypoint: parsed.entrypoint,
      };
    case 'stdout':
    case 'stderr':
    case 'system':
      if (typeof parsed.text !== 'string') {
        throw new Error('Invalid verifier text event.');
      }
      return {
        type: parsed.type,
        text: parsed.text,
      };
    case 'exit':
      return {
        type: 'exit',
        exitCode: typeof parsed.exitCode === 'number' ? parsed.exitCode : null,
        signal: typeof parsed.signal === 'number' ? parsed.signal : null,
        timedOut: parsed.timedOut === true,
        aborted: parsed.aborted === true,
        outputLimitExceeded: parsed.outputLimitExceeded === true,
        durationMs: typeof parsed.durationMs === 'number' ? parsed.durationMs : 0,
      };
    default:
      throw new Error(`Unsupported verifier event type: ${parsed.type}`);
  }
}

export function getVerifierStatusFromExit(event: Extract<VerifierStreamEvent, { type: 'exit' }>): VerifierRunStatus {
  if (event.aborted) {
    return 'aborted';
  }
  if (event.timedOut) {
    return 'timed_out';
  }
  if (event.exitCode === 0 && !event.outputLimitExceeded) {
    return 'success';
  }
  return 'failed';
}

function normalizePromptOutputResponse(data: unknown): VerifierPromptOutputResponse {
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid prompt.tex response.');
  }

  const output = typeof (data as { output?: unknown }).output === 'string'
    ? (data as { output: string }).output
    : null;

  if (!output || !output.trim()) {
    throw new Error('prompt.tex did not return any output.');
  }

  return {
    output,
    model: typeof (data as { model?: unknown }).model === 'string'
      ? (data as { model: string }).model
      : null,
  };
}

export async function generatePromptTexOutput(
  projectId: string,
  promptText: string,
  model?: string
): Promise<VerifierPromptOutputResponse> {
  const result = await runPromptTexFn({
    projectId,
    promptText,
    ...(model ? { model } : {}),
  });

  return normalizePromptOutputResponse(result.data);
}

async function getVerifierRunTicket(payload: VerifierRunPayload): Promise<VerifierRunTicketResponse> {
  const snapshotHash = await hashVerifierRunPayload(payload);
  const result = await createVerifierRunTicketFn({
    projectId: payload.projectId,
    entrypoint: payload.entrypoint,
    runMode: payload.runMode,
    snapshotHash,
    fileCount: payload.files.length,
    totalBytes: getVerifierPayloadSizeBytes(payload),
  });

  return result.data;
}

export async function streamVerifierRun(
  payload: VerifierRunPayload,
  options: {
    signal: AbortSignal;
    onEvent: (event: VerifierStreamEvent) => void;
  }
): Promise<void> {
  const ticket = await getVerifierRunTicket(payload);
  const serviceUrl = getVerifierServiceUrl();
  let response: Response;
  try {
    response = await fetch(`${serviceUrl}/runs/sync`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ticket: ticket.ticket,
        ...payload,
      }),
      signal: options.signal,
    });
  } catch (error) {
    if ((error as Error).name === 'AbortError') {
      throw error;
    }
    throw new Error(buildVerifierFetchErrorMessage(serviceUrl, error));
  }

  if (!response.ok) {
    const rawText = await response.text();
    let parsedMessage: string | null = null;
    try {
      const parsed = JSON.parse(rawText) as { error?: string };
      parsedMessage = parsed.error || null;
    } catch {
      parsedMessage = null;
    }
    throw new Error(parsedMessage || rawText || 'Verifier run failed to start.');
  }

  if (!response.body) {
    throw new Error('Verifier service did not return a response stream.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffered = '';

  while (true) {
    const { value, done } = await reader.read();
    buffered += decoder.decode(value || new Uint8Array(), { stream: !done });

    let newlineIndex = buffered.indexOf('\n');
    while (newlineIndex >= 0) {
      const line = buffered.slice(0, newlineIndex).trim();
      buffered = buffered.slice(newlineIndex + 1);
      if (line) {
        options.onEvent(parseVerifierStreamEvent(line));
      }
      newlineIndex = buffered.indexOf('\n');
    }

    if (done) {
      const finalLine = buffered.trim();
      if (finalLine) {
        options.onEvent(parseVerifierStreamEvent(finalLine));
      }
      return;
    }
  }
}
